import { PageContainer } from '@components/Layout/PageContainer';
import { DataTable } from '@components/DataTable';
import { ResponsiveButton } from '@components/ResponsiveButton';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { Add } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { CostItemEntryType, useBudgetItemsQuery } from 'gql/index';
import { useMoneyFormatter } from '@utils/numberUtils';
import { QuoteActions } from '../components/Quotes/QuoteActions';
import { QuoteDialog } from '../components/BudgetItemTable/components/QuoteDialog';
import dayjs from 'dayjs';

type QuoteRow = {
  id: number;
  name: string;
  costItemLabel: string;
  amount: number;
  quoteDate: string;
  costItemId: number;
  quote: {
    id: number;
    name: string;
    amount: number;
    quoteDate: string;
    attachments?: { id: number; fileName: string; thumbnailStatus?: string }[];
  };
};

export const ProjectQuotesPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const { projectId, canManageBudgetAndExpenses } = useCurrentProject();
  const formatMoney = useMoneyFormatter();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState<QuoteRow['quote'] | undefined>(undefined);
  const [editingCostItemId, setEditingCostItemId] = useState<number | undefined>(undefined);

  const { data: budgetItems = [], isFetching } = useBudgetItemsQuery(
    { projectId: projectId ?? 0 },
    { select: (d) => d.budgetItems, enabled: projectId != null },
  );

  const rows: QuoteRow[] = useMemo(() => {
    const result: QuoteRow[] = [];
    const ordered = [...budgetItems].sort((a, b) => a.order - b.order);
    for (const budgetItem of ordered) {
      const costItems = [...(budgetItem.costItems ?? [])].sort((a, b) => a.order - b.order);
      for (const costItem of costItems) {
        const entries = [...(costItem.entries ?? [])].sort((a, b) => a.order - b.order);
        const costItemLabel = `${budgetItem.name} › ${costItem.name}`;
        for (const entry of entries) {
          if (entry.type !== CostItemEntryType.Quote || !entry.quote) continue;
          const quote = entry.quote;
          result.push({
            id: quote.id,
            name: quote.name,
            costItemLabel,
            amount: Number(quote.amount),
            quoteDate: quote.quoteDate,
            costItemId: costItem.id,
            quote: {
              id: quote.id,
              name: quote.name,
              amount: Number(quote.amount),
              quoteDate: quote.quoteDate,
              attachments: quote.attachments ?? [],
            },
          });
        }
      }
    }
    return result.sort((a, b) => dayjs(b.quoteDate).valueOf() - dayjs(a.quoteDate).valueOf());
  }, [budgetItems]);

  const handleAddQuote = useCallback(() => {
    setEditingQuote(undefined);
    setEditingCostItemId(undefined);
    setDialogOpen(true);
  }, []);

  const handleEditQuote = useCallback((quote: QuoteRow['quote'], costItemId: number) => {
    setEditingQuote(quote);
    setEditingCostItemId(costItemId);
    setDialogOpen(true);
  }, []);

  const handleRowClick = useCallback((params: GridRowParams<QuoteRow>) => {
    setEditingQuote(params.row.quote);
    setEditingCostItemId(params.row.costItemId);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingQuote(undefined);
    setEditingCostItemId(undefined);
  }, []);

  const columns: GridColDef<QuoteRow>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: formatMessage({ id: 'Name' }),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'costItemLabel',
        headerName: formatMessage({ id: 'Cost item' }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'amount',
        headerName: formatMessage({ id: 'Amount' }),
        type: 'number',
        width: 120,
        valueFormatter: (value) => formatMoney(Number(value)),
      },
      {
        field: 'quoteDate',
        headerName: formatMessage({ id: 'Date' }),
        width: 120,
        valueFormatter: (value) => (value ? dayjs(String(value)).format('YYYY-MM-DD') : ''),
      },
      {
        field: 'actions',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <QuoteActions
              quote={row.quote}
              costItemId={row.costItemId}
              size="small"
              onEdit={handleEditQuote}
            />
          </div>
        ),
      },
    ],
    [formatMessage, formatMoney, handleEditQuote],
  );

  return (
    <PageContainer title={formatMessage({ id: 'Quotes' })} maxWidth="xl">
      <Stack gap={2}>
        {canManageBudgetAndExpenses && (
        <Stack direction="row" justifyContent="flex-end">
          <ResponsiveButton
            variant="contained"
            icon={<Add />}
            onClick={handleAddQuote}
          >
            {formatMessage({ id: 'Add quote' })}
          </ResponsiveButton>
        </Stack>
        )}
        <DataTable<QuoteRow>
          rows={rows}
          columns={columns}
          loading={isFetching}
          onRowClick={handleRowClick}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [{ field: 'quoteDate', sort: 'desc' }],
            },
          }}
          noDataMessage={formatMessage({ id: 'No quotes' })}
        />
      </Stack>
      <QuoteDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        costItemId={editingCostItemId}
        quote={editingQuote}
      />
    </PageContainer>
  );
};
